import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
	faBuilding as faBuildingRegular,
	faFileLines as faFileLinesRegular,
	faTrashCan as faTrashCanRegular,
	faUser as faUserRegular,
} from "@fortawesome/free-regular-svg-icons";
import {
	faArrowRightToBracket,
	faBuilding as faBuildingSolid,
	faChevronLeft,
	faChevronRight,
	faDesktop,
	faFileLines as faFileLinesSolid,
	faKey,
	faList,
	faPuzzlePiece,
	faShield,
	faShieldHalved,
	faTrashCan as faTrashCanSolid,
	faUser as faUserSolid,
	faUserGroup,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	Box,
	ButtonBase,
	Collapse,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Tooltip,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import axios from "axios";
import type { ReactElement } from "react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { useCollections } from "../settings/collections/CollectionsContext";
import coappIcon from "../../coapp-icon.png";
import apiRoutes from "../../constants/api-routes";
import pages from "../../constants/pages";
import { pickPluginDisplayNameFromPayload } from "../../helpers/pluginNavHelpers";
import useAuthHeader from "../../hooks/useAuthHeader";
import logoFull from "../../logo-full.svg";
import {
	selectOrganizationId,
	selectOrganizationIdentityProvider,
	selectOrganizationSsoAndScim,
} from "../../redux/userSlice";
import { NAV_DRAWER_TRANSITION_MS, NavMenuItem, SidebarNav } from "./styled/sidebar-navigation.styled";

// FontAwesome free regular doesn't include desktop/puzzle-piece/user-group/
// shield, so for those categories we lean on solid for both states and just
// switch weight via CSS (idle = thinner stroke via filter).
type NavIconPair = { regular: IconDefinition; solid: IconDefinition };
const NAV_ICONS = {
	rules:        { regular: faFileLinesRegular, solid: faFileLinesSolid },
	organization: { regular: faBuildingRegular,  solid: faBuildingSolid },
	users:        { regular: faUserRegular,      solid: faUserSolid },
	groups:       { regular: faUserGroup,        solid: faUsers },
	roles:        { regular: faShield,           solid: faShieldHalved },
	workstations: { regular: faDesktop,          solid: faDesktop },
	plugins:      { regular: faPuzzlePiece,      solid: faPuzzlePiece },
	collections:  { regular: faList,           solid: faList },
	// Sub-item icons (regular variants unavailable in the free set for these,
	// so solid is used for both states — opacity differentiates idle/selected).
	trash:        { regular: faTrashCanRegular,  solid: faTrashCanSolid },
	permissions:  { regular: faKey,              solid: faKey },
	sso:          { regular: faArrowRightToBracket, solid: faArrowRightToBracket },
} satisfies Record<string, NavIconPair>;

/** Render the FA icon variant based on selection. The SVG path fill is
 *  driven by the brand teal-to-blue <linearGradient id="coapp-grad-primary">
 *  injected at the WithNavRoute layer — selected items get full gradient,
 *  idle items get the same gradient at reduced opacity so they read as
 *  subordinate without a different color. */
const NavIcon = ({ kind, selected }: { kind: keyof typeof NAV_ICONS; selected: boolean }) => {
	const pair = NAV_ICONS[kind];
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				width: 20,
				height: 20,
				lineHeight: 0,
				opacity: 1,
				// Glyph sits a touch smaller within the same 20px slot so icon
				// spacing/alignment is unchanged, just the icon is lighter.
				"& svg": {
					width: 17,
					height: 17,
					display: "block",
				},
				"& svg path": {
					fill: "url(#coapp-grad-primary)",
				},
			}}
		>
			<FontAwesomeIcon icon={pair.solid} />
		</Box>
	);
};

/** Collapsed rail width (matches Figma icon rail). */
export const NAV_DRAWER_WIDTH_COLLAPSED = 50;

/** Responsive max width when the drawer is expanded (sm → xl). Tightened so
 * the canvas + content gets more horizontal real estate, matching the prototype. */
export function useNavDrawerExpandedWidth(): number {
	const theme = useTheme();
	const isXl = useMediaQuery(theme.breakpoints.up("xl"));
	const isLg = useMediaQuery(theme.breakpoints.up("lg"));
	const isMd = useMediaQuery(theme.breakpoints.up("md"));
	const isSm = useMediaQuery(theme.breakpoints.up("sm"));

	if (isXl) return 200;
	if (isLg) return 190;
	if (isMd) return 180;
	if (isSm) return 150;
	return 150;
}

type NavLeaf = {
	id: string;
	label: string;
	path: string;
	/** Sub-item icon so children match the parent rows' icon + label layout. */
	iconKind?: keyof typeof NAV_ICONS;
};

/** One resource (group / role / plugin) with nested tab links; `path` null = label-only row (no duplicate tab URL). */
type NavResourceNode = {
	id: string;
	label: string;
	path: string | null;
	children: NavLeaf[];
	/** Sub-item icon so the resource header matches the parent rows' layout. */
	iconKind?: keyof typeof NAV_ICONS;
};

type NavMenuEntry = NavLeaf | NavResourceNode;

type NavLink = {
	id: string;
	label: string;
	path: string;
	/** Optional FontAwesome icon kind. The variant rendered (regular vs
	 *  solid) is chosen per render based on the row's selected state. */
	iconKind?: keyof typeof NAV_ICONS;
	/** Legacy escape hatch for any caller still passing a static MUI icon. */
	icon?: ReactElement;
	children?: NavMenuEntry[];
};

function isNavResourceNode(entry: NavMenuEntry): entry is NavResourceNode {
	return "children" in entry && Array.isArray((entry as NavResourceNode).children);
}

function parseRoleResourceId(pathname: string): string | null {
	const m = pathname.match(/^\/settings\/roles\/([^/]+)\/(users|permissions|sso)$/);
	return m ? m[1] : null;
}

function parseGroupResourceId(pathname: string): string | null {
	const m = pathname.match(/^\/settings\/groups\/([^/]+)\/(users|rules|plugins|sso)$/);
	return m ? m[1] : null;
}

function parsePluginResourceId(pathname: string): string | null {
	const normalized = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
	const m = normalized.match(/^\/settings\/plugins\/([^/]+)(?:\/groups)?$/);
	return m ? m[1] : null;
}

function findPluginMetaInPlatformsPayload(
	platforms: unknown,
	pluginId: string,
): { name: string | null; isCustomerManaged: boolean | null } {
	if (!Array.isArray(platforms)) return { name: null, isCustomerManaged: null };
	const idStr = String(pluginId);
	for (const platform of platforms) {
		const plugins = (platform as { plugins?: unknown }).plugins;
		if (!Array.isArray(plugins)) continue;
		for (const plugin of plugins) {
			if (!plugin || typeof plugin !== "object") continue;
			const p = plugin as { id?: unknown; name?: unknown; isCustomerManaged?: unknown };
			if (String(p.id) !== idStr) continue;
			const name = typeof p.name === "string" && p.name.trim().length > 0 ? p.name.trim() : null;
			const isCustomerManaged =
				typeof p.isCustomerManaged === "boolean" ? p.isCustomerManaged : null;
			return { name, isCustomerManaged };
		}
	}
	return { name: null, isCustomerManaged: null };
}

function findPluginNameInPlatformsPayload(platforms: unknown, pluginId: string): string | null {
	return findPluginMetaInPlatformsPayload(platforms, pluginId).name;
}

function pickIsCustomerManagedFromDetailPayload(data: unknown): boolean | null {
	if (!data || typeof data !== "object") return null;
	const v = (data as Record<string, unknown>).isCustomerManaged;
	return typeof v === "boolean" ? v : null;
}

function idpGroupsChildLabel(identityProviderName: string | null): string {
	return identityProviderName
		? `Add/Remove ${identityProviderName} Groups`
		: "Add/Remove IdP Groups";
}

type SidebarNavigationProps = {
	isCollapsed: boolean;
	onToggle: () => void;
};

/** Controlled tooltip: closes when the drawer expands or when hover leaves (avoids stale MUI open state). */
function CollapsedNavTooltip({
	isCollapsed,
	title,
	children,
}: {
	isCollapsed: boolean;
	title: string;
	children: ReactElement;
}) {
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (!isCollapsed) {
			setOpen(false);
		}
	}, [isCollapsed]);

	return (
		<Tooltip
			open={isCollapsed ? open : false}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			title={title}
			placement="right"
			arrow
			disableInteractive
			disableFocusListener
			disableHoverListener={!isCollapsed}
			disableTouchListener={!isCollapsed}
		>
			{children}
		</Tooltip>
	);
}

const SidebarNavigation = ({ isCollapsed, onToggle }: SidebarNavigationProps) => {
	const theme = useTheme();
	const expandedWidth = useNavDrawerExpandedWidth();
	const navigate = useNavigate();
	const location = useLocation();
	const organizationSsoAndScim = useSelector(selectOrganizationSsoAndScim);
	const identityProviderName = useSelector(selectOrganizationIdentityProvider);
	const organizationId = useSelector(selectOrganizationId);
	const palette = theme.palette as any;
	// Inactive nav rows render against the sidebar's white surface — only
	// the SELECTED row gets the brand-teal gradient tint. Hover stays soft.
	const hoverBackground = "rgba(2, 213, 171, 0.06)";
	const tileBackground = "transparent";
	const authHeader = useAuthHeader();
	const { getCollectionById } = useCollections();

	const [fetchedGroupName, setFetchedGroupName] = useState<string | null>(null);
	const [fetchedRoleName, setFetchedRoleName] = useState<string | null>(null);
	const [fetchedPluginName, setFetchedPluginName] = useState<string | null>(null);
	/** `true` only when API reports customer-managed; otherwise sidebar defaults to the groups route. */
	const [fetchedPluginIsCustomerManaged, setFetchedPluginIsCustomerManaged] = useState<boolean | null>(null);

	const roleIdFromPath = parseRoleResourceId(location.pathname);
	const groupIdFromPath = parseGroupResourceId(location.pathname);
	const pluginIdFromPath = parsePluginResourceId(location.pathname);
	useEffect(() => {
		let cancelled = false;

		if (groupIdFromPath) {
			axios
				.get(apiRoutes.getGroupById(groupIdFromPath), { headers: authHeader })
				.then((res) => {
					if (cancelled) return;
					const name = (res.data as { name?: string })?.name;
					setFetchedGroupName(name ?? null);
				})
				.catch(() => {
					if (!cancelled) setFetchedGroupName(null);
				});
		} else {
			setFetchedGroupName(null);
		}

		if (roleIdFromPath) {
			axios
				.get(apiRoutes.getRoleById(roleIdFromPath), { headers: authHeader })
				.then((res) => {
					if (cancelled) return;
					const name = (res.data as { name?: string })?.name;
					setFetchedRoleName(name ?? null);
				})
				.catch(() => {
					if (!cancelled) setFetchedRoleName(null);
				});
		} else {
			setFetchedRoleName(null);
		}

		if (pluginIdFromPath) {
			void (async () => {
				let name: string | null = null;
				let isCustomerManaged: boolean | null = null;
				try {
					const res = await axios.get(apiRoutes.getPlugin(pluginIdFromPath), { headers: authHeader });
					if (cancelled) return;
					name = pickPluginDisplayNameFromPayload(res.data);
					isCustomerManaged = pickIsCustomerManagedFromDetailPayload(res.data);
				} catch {
					/* fall through to platforms list */
				}
				if ((!name || isCustomerManaged === null) && organizationId != null) {
					try {
						const platRes = await axios.get(
							apiRoutes.getPlatformsAndPlugins(String(organizationId)),
							{ headers: authHeader },
						);
						if (cancelled) return;
						const meta = findPluginMetaInPlatformsPayload(platRes.data, pluginIdFromPath);
						if (!name) name = meta.name;
						if (isCustomerManaged === null) isCustomerManaged = meta.isCustomerManaged;
					} catch {
						/* ignore */
					}
				}
				if (!cancelled) {
					setFetchedPluginName(name);
					setFetchedPluginIsCustomerManaged(isCustomerManaged);
				}
			})();
		} else {
			setFetchedPluginName(null);
			setFetchedPluginIsCustomerManaged(null);
		}

		return () => {
			cancelled = true;
		};
	}, [groupIdFromPath, roleIdFromPath, pluginIdFromPath, authHeader, organizationId]);

	const navItems: NavLink[] = useMemo(() => {
		const roleId = parseRoleResourceId(location.pathname);
		const groupId = parseGroupResourceId(location.pathname);
		const pluginId = parsePluginResourceId(location.pathname);

		const roleTabLeaves: NavLeaf[] | undefined = roleId
			? [
				{ id: "role-users", label: "Users", path: pages.roleUsers(roleId), iconKind: "users" },
				{ id: "role-permissions", label: "Permissions", path: pages.rolePermissions(roleId), iconKind: "permissions" },
				...(organizationSsoAndScim
					? [
						{
							id: "role-sso",
							label: idpGroupsChildLabel(identityProviderName),
							path: pages.roleSSO(roleId), iconKind: "sso" as const,
						},
					]
					: []),
			]
			: undefined;

		const roleChildren: NavMenuEntry[] | undefined =
			roleId && roleTabLeaves
				? [
					{
						id: `role-${roleId}`,
						label: fetchedRoleName ?? `Role ${roleId}`,
						path: null,
						children: roleTabLeaves, iconKind: "roles",
					},
				]
				: undefined;

		const groupTabLeaves: NavLeaf[] | undefined = groupId
			? [
				{ id: "group-users", label: "Users", path: pages.groupUsers(groupId), iconKind: "users" },
				{ id: "group-rules", label: "Rules", path: pages.groupRules(groupId), iconKind: "rules" },
				{ id: "group-plugins", label: "Plugins", path: pages.groupPlugins(groupId), iconKind: "plugins" },
				...(organizationSsoAndScim
					? [
						{
							id: "group-sso",
							label: idpGroupsChildLabel(identityProviderName),
							path: pages.groupSSO(groupId), iconKind: "sso" as const,
						},
					]
					: []),
			]
			: undefined;

		const groupChildren: NavMenuEntry[] | undefined =
			groupId && groupTabLeaves
				? [
					{
						id: `group-${groupId}`,
						label: fetchedGroupName ?? `Group ${groupId}`,
						path: null,
						children: groupTabLeaves, iconKind: "groups",
					},
				]
				: undefined;

		const pluginChildren: NavMenuEntry[] | undefined = pluginId
			? [
				{
					id: `plugin-${pluginId}`,
					label: fetchedPluginName ?? `Plugin ${pluginId}`,
					path:
							fetchedPluginIsCustomerManaged === true
								? pages.pluginHome(pluginId)
								: pages.pluginGroups(pluginId),
					children: [{ id: "plugin-groups", label: "Groups", path: pages.pluginGroups(pluginId), iconKind: "groups" }], iconKind: "plugins",
				},
			]
			: undefined;
		
		function parseCollectionResourceId(pathname: string): string | null {
			const normalized = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
			if (normalized === "/collections") return null;
			const m = normalized.match(/^\/collections\/([^/]+)$/);
			return m ? m[1] : null;
		}
		
		const collectionId = parseCollectionResourceId(location.pathname);
		const activeCollection = collectionId ? getCollectionById(collectionId) : undefined;
		const collectionChildren: NavLeaf[] | undefined =
			collectionId && activeCollection
				? [
					{
						id: `collection-${collectionId}`,
						label: activeCollection.name,
						path: pages.collection(collectionId),
						iconKind: "collections",
					},
				]
				: undefined;

		const organizationChildren: NavLeaf[] | undefined = organizationSsoAndScim
			? [
				{
					id: "org-sso-scim",
					label: "SSO + SCIM Configuration",
					path: pages.scimWizard, iconKind: "sso" as const,
				},
			]
			: undefined;

		return [
			{
				id: "library",
				label: "Rules",
				path: pages.home,
				iconKind: "rules",
				children: [
					{
						id: "trash",
						label: "Trash",
						path: "/rules/trash", iconKind: "trash" as const,
					},
				],
			},
			{
				id: "organization",
				label: "Organization",
				path: pages.organizationManagement,
				iconKind: "organization",
				...(organizationChildren?.length ? { children: organizationChildren } : {}),
			},
			{
				id: "users",
				label: "Users",
				path: pages.userManagement,
				iconKind: "users",
			},
			{
				id: "groups",
				label: "Groups",
				path: pages.groupManagement,
				iconKind: "groups",
				...(groupChildren?.length ? { children: groupChildren } : {}),
			},
			{
				id: "roles",
				label: "Roles",
				path: pages.roleManagement,
				iconKind: "roles",
				...(roleChildren?.length ? { children: roleChildren } : {}),
			},
			{
				id: "workstations",
				label: "Workstations",
				path: pages.workstationManagement,
				iconKind: "workstations",
			},
			{
				id: "plugins",
				label: "Plugins",
				path: pages.pluginManagement,
				iconKind: "plugins",
				...(pluginChildren?.length ? { children: pluginChildren } : {}),
			},
			{
				id: "collections",
				label: "Collections",
				path: pages.collectionManagement,
				iconKind: "collections",
				...(collectionChildren?.length ? { children: collectionChildren } : {}),
			},
		];
	}, [
		location.pathname,
		organizationSsoAndScim,
		identityProviderName,
		fetchedGroupName,
		fetchedRoleName,
		fetchedPluginName,
		fetchedPluginIsCustomerManaged,
		getCollectionById,
	]);

	const drawerWidth = isCollapsed ? NAV_DRAWER_WIDTH_COLLAPSED : expandedWidth;
	const navItemHeight = 42;
	/** Room for icon + padding; animates in sync with drawer width so labels wipe open left → right. */
	const navParentLabelMaxWidthPx = Math.max(0, expandedWidth - 52);

	const navLabelRevealTransition = theme.transitions.create(["max-width", "margin-left"], {
		duration: NAV_DRAWER_TRANSITION_MS,
		easing: theme.transitions.easing.easeInOut,
	});

	/** Full string width inside clip; parent overflow clips — no ellipsis, smooth horizontal reveal. */
	const navLabelListItemTextSx = {
		my: 0,
		overflow: "visible",
		"& .MuiTypography-root": {
			display: "block",
			width: "max-content",
			maxWidth: "none",
			whiteSpace: "nowrap",
			overflow: "visible",
			textOverflow: "clip",
		},
	} as const;

	/**
	 * Parent `NavMenuItem` selected gradient uses alpha 0.08; child rows stay subtler;
	 * selected-hover sits between child-selected and parent (never reaches parent opacity).
	 */
	const navChildGradientHover = "linear-gradient(90deg, rgba(1, 213, 171, 0.04) 0%, rgba(0, 114, 174, 0.04) 100%)";
	const navGradientText = {
		backgroundImage: "linear-gradient(90deg, #01D5AB 0%, #0072AE 100%)",
		WebkitBackgroundClip: "text",
		backgroundClip: "text",
		color: "transparent",
		WebkitTextFillColor: "transparent",
	} as const;

	const matchesPath = (target: string) => {
		const normalizedTarget = target.endsWith("/") ? target.slice(0, -1) : target;
		const normalizedPath = location.pathname.endsWith("/")
			? location.pathname.slice(0, -1)
			: location.pathname;

		return normalizedPath === normalizedTarget || normalizedPath.startsWith(`${normalizedTarget}/`);
	};

	const matchesNavMenuEntry = (entry: NavMenuEntry) => {
		if (isNavResourceNode(entry)) {
			return (
				Boolean(entry.path && matchesPath(entry.path))
				|| entry.children.some((leaf) => matchesPath(leaf.path))
			);
		}
		return matchesPath(entry.path);
	};

	/** Parent row uses selected styling only when the route matches the parent path, not a child path (prefix match). */
	const isParentNavRowSelected = (item: NavLink) => {
		const anyChildMatches = item.children?.some((child) => matchesNavMenuEntry(child)) ?? false;
		return matchesPath(item.path) && !anyChildMatches;
	};

	const handleItemClick = (item: NavLink) => {
		if (isCollapsed) {
			onToggle();
		}
		if (item.path) {
			navigate(item.path);
		}
	};

	const handleLeafClick = (leaf: NavLeaf) => {
		navigate(leaf.path);
	};

	const leafListItemButtonSx = {
		borderRadius: 0,
		py: 0.5,
		// Indent so the sub-row icon's left edge lines up with the START of the
		// parent rows' labels (row padding 8 + icon box 40 ≈ where titles begin),
		// making the child clearly subordinate to its parent.
		pl: "44px",
		pr: 1.25,
		mb: 0.25,
	} as const;

	const renderSubRow = (opts: {
		key: string;
		label: string;
		active: boolean;
		iconKind?: keyof typeof NAV_ICONS;
		onClick?: () => void;
		clickable: boolean;
	}) => {
		const { key, label, active, iconKind, onClick, clickable } = opts;
		const content = (
			<>
				<ListItemIcon
					sx={{
						minWidth: 40,
						width: 40,
						justifyContent: "center",
						color: active ? theme.palette.primary.dark : "rgba(13, 31, 48, 0.65)",
					}}
				>
					{iconKind ? <NavIcon kind={iconKind} selected={active} /> : null}
				</ListItemIcon>
				<Box
					aria-hidden={isCollapsed}
					sx={{
						flex: "1 1 auto",
						minWidth: 0,
						overflow: "hidden",
						maxWidth: isCollapsed ? 0 : navParentLabelMaxWidthPx,
						ml: isCollapsed ? 0 : 0.75,
						transition: navLabelRevealTransition,
					}}
				>
					<ListItemText
						primary={label}
						sx={navLabelListItemTextSx}
						slotProps={{
							primary: {
								variant: "body1",
								sx: { fontWeight: active ? 600 : 500, ...(active ? navGradientText : {}) },
							},
						}}
					/>
				</Box>
			</>
		);
		if (!clickable) {
			return (
				<ListItem key={key} sx={{ ...leafListItemButtonSx, display: "flex", alignItems: "center", background: "transparent" }}>
					{content}
				</ListItem>
			);
		}
		return (
			<ListItemButton
				key={key}
				onClick={onClick}
				selected={active}
				sx={{
					...leafListItemButtonSx,
					color: active ? theme.palette.primary.dark : "rgba(13, 31, 48, 0.86)",
					background: "transparent",
					":hover": { background: navChildGradientHover },
					"&.Mui-selected": { background: "transparent" },
					"&.Mui-selected:hover": { background: navChildGradientHover },
				}}
			>
				{content}
			</ListItemButton>
		);
	};

	const renderNavMenuEntries = (entries: NavMenuEntry[]) =>
		entries.map((entry) => {
			if (isNavResourceNode(entry)) {
				const resourceSubtreeActive =
					Boolean(entry.path && matchesPath(entry.path))
					|| entry.children.some((leaf) => matchesPath(leaf.path));
				return (
					<Box key={entry.id} sx={{ width: "100%" }}>
						{renderSubRow({
							key: `${entry.id}-row`,
							label: entry.label,
							active: resourceSubtreeActive,
							iconKind: entry.iconKind,
							onClick: entry.path ? () => navigate(entry.path!) : undefined,
							clickable: Boolean(entry.path),
						})}
						<List disablePadding sx={{ pl: 0, pb: 0.25 }}>
							{entry.children.map((leaf) =>
								renderSubRow({
									key: leaf.id,
									label: leaf.label,
									active: matchesPath(leaf.path),
									iconKind: leaf.iconKind,
									onClick: () => handleLeafClick(leaf),
									clickable: true,
								})
							)}
						</List>
					</Box>
				);
			}
			return renderSubRow({
				key: entry.id,
				label: entry.label,
				active: matchesPath(entry.path),
				iconKind: entry.iconKind,
				onClick: () => handleLeafClick(entry),
				clickable: true,
			});
		});

	const handleLogoClick = () => {
		if (isCollapsed) {
			onToggle();
		}
		navigate(pages.home);
	};

	return (
		<SidebarNav
			$width={drawerWidth}
			variant="permanent"
			open
			anchor="left"
		>
			<Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", mx: 0, px: 0 }}>
				{/* SVG gradient definitions */}
				<svg width="0" height="0">
					<defs>
						<linearGradient id="navIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
							<stop offset="0%" stopColor="#01D5AB" />
							<stop offset="100%" stopColor="#0072AE" />
						</linearGradient>
						<linearGradient id="navBorderGradient" x1="0%" y1="0%" x2="0%" y2="100%">
							<stop offset="0%" stopColor="#01D5AB" />
							<stop offset="100%" stopColor="#0072AE" />
						</linearGradient>
					</defs>
				</svg>

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						width: "100%",
						height: 64,
						alignItems: isCollapsed ? "center" : "flex-start",
						mb: 1,
						px: 0,
					}}
				>
					<ButtonBase
						onClick={handleLogoClick}
						sx={{
							borderRadius: "12px",
							py: 0.75,
							// Align the logo's left edge with the LEFT EDGE of the
							// nav icons below. Row padding is 8px and the 40px icon
							// box centers a 20px glyph, so the icon's left edge sits
							// at 8 + (40-20)/2 = 18px — match that here. Collapsed
							// mode keeps the icon centered in the rail.
							pl: isCollapsed ? 0 : "18px",
							pr: isCollapsed ? 0 : 1,
							justifyContent: isCollapsed ? "center" : "flex-start",
							alignSelf: isCollapsed ? "center" : "flex-start",
							":hover": { backgroundColor: hoverBackground },
						}}
						aria-label="Go to rule library"
					>
						<Box
							component="img"
							src={isCollapsed ? coappIcon : logoFull}
							alt="CoApp"
							sx={{
								height: 24,
								width: isCollapsed ? 24 : "auto",
								objectFit: "contain",
								display: "block",
							}}
						/>
					</ButtonBase>
				</Box>

				<List
					disablePadding
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 0.25,
						flexShrink: 0,
						width: "100%",
					}}
				>
					{navItems.map((item) => {
						const isParentSelected = isParentNavRowSelected(item);
						const isSubtreeActive =
							!!item.children?.length
							&& (matchesPath(item.path) || item.children.some((child) => matchesNavMenuEntry(child)));

						const button = (
							<NavMenuItem
								onClick={() => handleItemClick(item)}
								selected={isParentSelected}
								$isCollapsed={isCollapsed}
								$navItemHeight={navItemHeight}
								$isActive={isParentSelected}
								$tileBackground={tileBackground}
							>
								<ListItemIcon
									sx={{
										minWidth: 40,
										width: 40,
										justifyContent: "center",
										color: isParentSelected ? theme.palette.primary.dark : "rgba(13, 31, 48, 0.65)",
									}}
								>
									{item.iconKind
										? <NavIcon kind={item.iconKind} selected={isParentSelected} />
										: item.icon}
								</ListItemIcon>
								<Box
									aria-hidden={isCollapsed}
									sx={{
										flex: "1 1 auto",
										minWidth: 0,
										overflow: "hidden",
										maxWidth: isCollapsed ? 0 : navParentLabelMaxWidthPx,
										ml: isCollapsed ? 0 : 0.75,
										transition: navLabelRevealTransition,
									}}
								>
									<ListItemText
										primary={item.label}
										sx={navLabelListItemTextSx}
										slotProps={{
											primary: {
												variant: "body1",
												sx: { fontWeight: isParentSelected ? 600 : 500, ...(isParentSelected ? navGradientText : {}) },
											},
										}}
									/>
								</Box>
							</NavMenuItem>
						);

						return (
							<Box key={item.id} sx={{ width: "100%" }}>
								<CollapsedNavTooltip isCollapsed={isCollapsed} title={item.label}>
									<Box
										sx={{
											display: "block",
											width: "100%",
											minWidth: 0,
										}}
									>
										{button}
									</Box>
								</CollapsedNavTooltip>
								{item.children?.length ? (
									<Collapse in={!isCollapsed && (isSubtreeActive || item.id === "library")} timeout="auto" unmountOnExit>
										<List disablePadding sx={{ pl: 0, pt: 0.5, pb: 0.25 }}>
											{renderNavMenuEntries(item.children)}
										</List>
									</Collapse>
								) : null}
							</Box>
						);
					})}
				</List>
				{/* QA: previous toggle was an invisible click area — reviewers
				    couldn't find it. Replaced with a visible chevron pill
				    that sits at the rail's bottom-right edge (or centered
				    when collapsed), with hover + tooltip. */}
				<Box sx={{ flexGrow: 1, position: "relative", minHeight: 48 }}>
					<Tooltip
						title={isCollapsed ? "Expand navigation" : "Collapse navigation"}
						placement="right"
						arrow
					>
						<ButtonBase
							onClick={onToggle}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									onToggle();
								}
							}}
							aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
							sx={{
								position: "absolute",
								bottom: 12,
								// Centered when collapsed, hugs the right
								// edge when expanded so it reads as "tuck
								// this away to the left."
								...(isCollapsed
									? { left: "50%", transform: "translateX(-50%)" }
									: { right: 10 }),
								width: 28,
								height: 28,
								borderRadius: "50%",
								background: "rgba(2, 213, 171, 0.10)",
								border: "1px solid rgba(2, 213, 171, 0.25)",
								color: "#057A6D",
								boxShadow: "0 1px 3px rgba(13, 31, 48, 0.06)",
								transition: "background 0.15s, border-color 0.15s, transform 0.15s",
								"&:hover": {
									background: "rgba(2, 213, 171, 0.18)",
									borderColor: "rgba(2, 213, 171, 0.45)",
									transform: isCollapsed
										? "translateX(-50%) scale(1.06)"
										: "scale(1.06)",
								},
								"&:focus-visible": {
									outline: `2px solid ${theme.palette.primary.main}`,
									outlineOffset: 2,
								},
							}}
						>
							<FontAwesomeIcon
								icon={isCollapsed ? faChevronRight : faChevronLeft}
							/>
						</ButtonBase>
					</Tooltip>
				</Box>
			</Box>
		</SidebarNav>
	);
};

export default SidebarNavigation;
